'use client'
import React, { HTMLAttributes, ReactElement, useState } from 'react'
import { Button, ButtonProps, ButtonVariants } from '../../atoms'

export type SwitcherId = string | number

export interface SwitcherProps extends HTMLAttributes<HTMLDivElement> {
    /** array of button texts */
    items: SwitcherItem[]
    /** id of initially active item */
    initialItem?: SwitcherId | null
    /** whether switch is disabled */
    disabled?: boolean
    /** callback on switch action */
    onSwitch?: (id: string | number) => void
    isLoading?: boolean
    isStretched?: boolean
}

export interface SwitcherItem extends Partial<Omit<ButtonProps, 'id'>> {
    id: SwitcherId
    label: string
}

export const Switcher = ({
    items = [],
    initialItem,
    disabled = false,
    onSwitch,
    isLoading = false,
    isStretched = false,
    ...props
}: SwitcherProps): ReactElement => {
    const [activePosition, setActivePosition] = useState<SwitcherId | undefined>(initialItem || items[0]?.id)

    if (isLoading) return <div className="bg-shade-opacity-100 h-8 density-md:h-12 w-72 rounded-default" />
    if (items?.length === 0) return <></>

    const clickHandler = (id: SwitcherId): void => {
        if (isActive(id) || disabled) return // ignore if item is already active or switch is disabled
        setActivePosition(id)
        if (onSwitch) onSwitch(id)
    }

    const isActive = (id: SwitcherId): boolean => activePosition === id

    const getRootClasses = () => {
        let classes = [
            'grid grid-flow-col bg-shade-0 outline-1 outline-shade-200 p-1 h-8 density-md:h-10 density-lg:h-12',
        ]
        classes.push(isStretched ? 'w-full auto-cols-fr' : 'w-fit')
        props?.className && classes.push(props.className)
        return classes.join(' ')
    }

    return (
        <div className={getRootClasses()}>
            {items?.length > 0 &&
                items.map((item: SwitcherItem) => (
                    <Button
                        key={`item-${item.id}`}
                        variant={isActive(item.id) ? item.variant || ButtonVariants.SECONDARY : ButtonVariants.QUIET}
                        disabled={disabled || item.disabled}
                        onClick={() => clickHandler(item.id)}
                        className={`${isActive(item.id) ? 'text-text-primary' : 'text-text-secondary'}`}
                        contained
                        data-testid={`switcher-item-${item.id}`}
                    >
                        {item.label || item.children}
                    </Button>
                ))}
        </div>
    )
}
